import { background, Box, Button, ButtonSpinner, CloseButton, Heading, HStack, IconButton, Input, InputGroup, InputRightElement, Text, VStack } from "@chakra-ui/react";
import { ResultListDto } from "../../../../Api/client/ResultClient";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spacer
} from "@chakra-ui/react"
import { Croissant, Cross, FileSearch, FileSearchIcon, SearchIcon } from "lucide-react";


function formatTime(ms: number) {
  if (!ms) return "-"

  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export default function ResultSearchBox({ resultLists }: { resultLists: ResultListDto[] }) {

  const [search, setSearch] = useState("");
  const [lastExecutedSearch, setLastExecutedSearch] = useState<null|string>(null);
  const [hits, setHits] = useState<{
    name: string;
    team: string;
    disciplineName: string;
    distance: number;
    timeInMs: number;
    meanSpeedInKmPerH: number;
    resultListPlacement: number;
    resultListName: string;
    startNr: number;
}[]>([]);

  const findHits = useCallback((search: string) => {
    if (!search) return []

    const lower = search.toLowerCase().trim();

    const matches: {
      name: string
      team: string
      disciplineName: string
      distance: number
      timeInMs: number
      meanSpeedInKmPerH: number
      resultListPlacement: number
      resultListName: string
      startNr: number
    }[] = []

    resultLists.forEach((list) => {
      list.results.forEach((r, ri) => {

        const name = `${r.firstname} ${r.lastname}`
        const team = r.clubOrTeam?.trim() || "-"
        const disciplineName = r.disciplineName
        const distance = r.distanceInMeters || 0
        const startNr = r.startnumber || 0;

        const timeInMs =
          r.finishTime != null && r.startTime != null ? r.finishTime - r.startTime : 0

        const meanSpeedInKmPerH =
          timeInMs === 0
            ? 0
            : (distance / 1000) / (timeInMs / (1000 * 60 * 60))

        const resultListPlacement = ri + 1
        const resultListName = list.name

        const nameLower = name.toLowerCase()
        const teamLower = team.toLowerCase()
        const startnr = `${r.startnumber}`;

        const isPureNumberSearch = !!search.match(/^\s*\d+\s*$/)

        if (isPureNumberSearch ? startnr === lower : (nameLower.includes(lower) || teamLower.includes(lower))) {
          matches.push({
            name,
            team,
            disciplineName,
            distance,
            timeInMs,
            meanSpeedInKmPerH,
            resultListPlacement,
            resultListName, 
            startNr
          })
        }
      })
    })

    return matches
  }, [resultLists])

  useEffect(() => {
    setHits(findHits(search));
  }, [resultLists]);


  return (
    <Box
        bg="white"
        borderRadius="10px"
        borderColor="gray.300"
        borderWidth="1px"
        m="10px auto"
        p="16px"
        maxW="900px"
        w="100%">

      <Heading size={"md"}>Sök resultat</Heading>

      <VStack spacing={3} align="stretch">


        {/* SEARCH BAR */}

        <HStack>
            <InputGroup>
                <Input
                    placeholder="Sök resultat"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            <InputRightElement bg={"blue.100"} borderRadius={"0px 5px 5px 0px"}>
                   {
                    hits.length > 0 && lastExecutedSearch == search 
                    ? <CloseButton onClick={() => {
                            setHits([]);
                            setSearch("");
                            setLastExecutedSearch(null)
                        }
                    } />
                    : <IconButton
                        aria-label="Sök"
                        onClick={() => { 
                            if (search.length > 1 || search.match(/^\s*\d+\s*$/)) {
                                setHits(findHits(search)); 
                                setLastExecutedSearch(search);
                            }
                        }}
                        icon={<SearchIcon color="black"/>}
                        height={"100%"}
                        variant="link"  
                        _hover={{bg: "blue.200",}}
                        _active={{ bg: "blue.300",}}
                    />
                   }
                </InputRightElement>
            </InputGroup>

                {/*
                        <Text fontSize="sm" w="80px">
                            {hits.length} st
                        </Text>
                */}
        </HStack>

        <Box maxH="350px" overflowY="auto" w="100%">

            {hits.length > 0 && (
            <Accordion allowToggle allowMultiple>

                {hits.map((hit, i) => (

                <AccordionItem key={`${hit.startNr}-${hit.disciplineName}-${hit.resultListName}`} border="1px solid" borderColor="gray.200" borderRadius="6px" mb={1}>
                    <AccordionButton >
                    <HStack w="100%" fontSize="sm">
                      <Text fontWeight="bold" minW="70px" textAlign={"left"}> {hit.resultListName} </Text>
                        <Text flex="1" textAlign={"left"}> {hit.name} </Text>
                        <Text minW="60px" textAlign="right"> {formatTime(hit.timeInMs)} </Text>
                        <AccordionIcon />
                    </HStack>
                    </AccordionButton>

                    <AccordionPanel pb={3} fontSize="sm">
                    <VStack align="start" spacing={1}>
                        <Text> <b>Nr:</b> {hit.startNr} </Text>
                        <Text> <b>Placering:</b> {hit.resultListPlacement} </Text>
                        <Text> <b>Tid:</b> {formatTime(hit.timeInMs)}</Text>
                        <Text> <b>Distans:</b> {(hit.distance/1000).toFixed(1)} km </Text>
                        <Text> <b>Tempo:</b> {meanSpeedToPace(hit.meanSpeedInKmPerH)} per km</Text>
                        <Text> <b>Medelhastighet:</b> {hit.meanSpeedInKmPerH.toFixed(2)} km/h </Text>
                        <Text><b>Lag/Klubb:</b> {hit.team}</Text>
                    </VStack>
                    </AccordionPanel>
                </AccordionItem>
                ))}
            </Accordion>
            )}
        </Box>
      </VStack>
    </Box>
  )
}

function meanSpeedToPace(meanspeedInKmPerH: number) {
  const paceMinutes = 60 / meanspeedInKmPerH;
  let minutes = Math.floor(paceMinutes);
  let seconds = Math.round((paceMinutes - minutes) * 60);

  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}